import React, { useCallback, useEffect, useState } from 'react';

import { exportToCanvas } from '@excalidraw/excalidraw';
import { ExcalidrawImperativeAPI } from '@excalidraw/excalidraw/types/types';
import { DialogProps } from '@material-ui/core';
import { Download } from '@mui/icons-material';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material';
import { fileSave } from 'browser-fs-access';
import { useTranslation } from 'react-i18next';

const canvasToBlob = async (
    canvas: HTMLCanvasElement
): Promise<Blob> => new Promise((resolve, reject) => {
    try {
        canvas.toBlob((blob) => {
            if (!blob) {
                // TODO: error
                return reject();
            }
            resolve(blob);
        });
    } catch (error: any) {
        reject(error);
    }
});

type ExportModalProps = DialogProps & {
    excalidrawAPI: ExcalidrawImperativeAPI;
    onClose: () => void;
};

export const ExportModal = (props: ExportModalProps) => {
    const { t } = useTranslation(['whiteboard', 'common']);
    const { excalidrawAPI, onClose } = props;

    const [previewNode, setPreviewNode] = useState<HTMLCanvasElement | undefined>();

    const getCanvas = useCallback(async () => {
        const canvas = await exportToCanvas({
            elements: excalidrawAPI.getSceneElements().filter((e) => e.type !== 'image'),
            appState: excalidrawAPI.getAppState(),
            files: excalidrawAPI.getFiles()
        });
        canvas.style.maxWidth = '100%';
        canvas.style.maxHeight = '50vh';
        // if converting to blob fails, there's some problem that will
        // likely prevent preview and export (e.g. canvas too big)
        await canvasToBlob(canvas);
        return canvas;
    }, [excalidrawAPI]);

    const getPreview = useCallback(async () => {
        if (!previewNode) {
            return;
        }
        const canvas = await getCanvas();
        previewNode.appendChild(canvas);
    }, [getCanvas, previewNode]);

    const download = useCallback(async () => {
        const canvas = await getCanvas();
        canvasToBlob(canvas).then(async (blob) => {
            try {
                await fileSave(blob);
            } catch (e) {
                if ((e as any).message && (e as any).message.includes('The user aborted a request.')) {
                    return;
                }
                throw e;
            }
        });

        onClose();
    }, [getCanvas, onClose]);

    useEffect(() => {
        if (!previewNode) {
            return;
        }

        getPreview();
    }, [getPreview, previewNode]);

    const refCallback = useCallback((node) => {
        setPreviewNode(node);
    }, []);

    return (
        <Dialog
            open={props.open}
            maxWidth="md"
            fullWidth={true}
            onClose={onClose}
        >
            <DialogTitle>{t('whiteboard:downloadWhiteboard')}</DialogTitle>
            <DialogContent>
                <div ref={refCallback} />
            </DialogContent>
            <DialogActions>
                <Button variant="outlined" color="primary" onClick={onClose}>{t('common:cancel')}</Button>
                <Button variant="contained" color="primary" onClick={download} startIcon={<Download />}>
                    {t('whiteboard:downloadPng')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};
